import {
  AbstractOperator,
  QueryOperator,
  Aggregation,
  CaseStatus,
  CaseType,
  ClausalCondition,
  ElasticStatsSearchPayload,
  ElasticStatsSearchResponse,
  Filter,
  FilterTreeBranch,
  FilterTreeNode,
  GenericCondition,
  Queue,
  User,
} from "@coherehealth/qm-api";
import { unionBy } from "lodash";
import { addDays, addHours, endOfDay, parseISO } from "date-fns";
import { startOfDate } from "@coherehealth/common";
import addMinutes from "date-fns/addMinutes";
import { ChartItem } from "./ChartUtil";

export type AggregationData =
  | {
      [key: string]: {
        count?: number;
        value?: number;
        aggregations?: AggregationData;
      };
    }
  | undefined;

export type AggregatedData =
  | {
      [key: string]: number;
    }
  | undefined;

// Mapping of QM queue filter operators to ElasticSearch (ES) operators
export const queryToAbstractOperatorMapping: { [key in QueryOperator]: AbstractOperator | null } = {
  EQ: "eq",
  NEQ: "neq",
  IN: "inlist",
  NIN: "ninlist",
  IS_NULL: null,
  // The backend gates "prefix" on the qmQueueFilterStartsWith flag (flag OFF or error => matches nothing),
  // so the frontend never checks the flag.
  STARTS_WITH: "prefix",
};

const filterTreeOperatorToClause: { [key in FilterTreeBranch["operator"]]: ClausalCondition["clause"] } = {
  AND: "MUST",
  OR: "SHOULD",
  NOT_ANY: "MUST_NOT",
};

// Recursively convert a filterTree node into ClausalConditions
export const convertFilterTreeToClausalConditions = (node: FilterTreeNode): ClausalCondition[] => {
  if (node.type === "Leaf") {
    const operator = queryToAbstractOperatorMapping[node.operator];
    if (!operator) {
      return [];
    }
    const value = node.filterValue ?? node.filterValues ?? [];
    return [{ clause: "MUST", conditions: [{ key: `${node.attribute}.keyword`, operator, value }] }];
  }

  const clause = filterTreeOperatorToClause[node.operator];
  const leafConditions: GenericCondition[] = [];
  const nestedGroups: ClausalCondition[][] = [];

  for (const child of node.children) {
    if (child.type === "Leaf") {
      const operator = queryToAbstractOperatorMapping[child.operator];
      if (!operator) {
        continue;
      }
      const value = child.filterValue ?? child.filterValues ?? [];
      leafConditions.push({ key: `${child.attribute}.keyword`, operator, value });
    } else {
      // Each Tree child becomes a nested group/a self-contained list of ClausalConditions
      // that the backend builds into its own BoolQueryBuilder under the parent clause.
      // A group whose leaves were all skipped (e.g. only IS_NULL) is dropped: sending an empty group
      // (nestedConditions: [[]]) makes the stats request fail with a 400.
      const nestedGroup = convertFilterTreeToClausalConditions(child);
      if (nestedGroup.length > 0) {
        nestedGroups.push(nestedGroup);
      }
    }
  }

  const result: ClausalCondition[] = [];
  if (leafConditions.length > 0 || nestedGroups.length > 0) {
    result.push({
      clause,
      conditions: leafConditions,
      nestedConditions: nestedGroups.length > 0 ? nestedGroups : undefined,
    });
  }
  return result;
};

type QueueFilterSource = Pick<Queue, "filters" | "filterTree" | "useFilterTree">;

// Returns the ClausalConditions derived from the queue's effective filter source
export const getQueueFilterClauses = (queue: QueueFilterSource): ClausalCondition[] => {
  if (queue.useFilterTree && queue.filterTree) {
    return convertFilterTreeToClausalConditions(queue.filterTree);
  }
  return mappedQueueFilterBlocks(queue.filters || []).map((condition) => ({
    clause: "MUST" as ClausalCondition["clause"],
    conditions: [condition],
  }));
};

// Ordering for the Reports Dashboard - Stats Bar
export const HUMANA_ORDERED_STATS_LIST_TO_CASE_TYPE_MAPPING: {
  [key: string]: CaseType[];
}[] = [
  { "Fax Intake": ["FAX_INTAKE"] },
  { "Clinical Review": ["RN_REVIEW"] },
  { "MD & P2P Review": ["MD_REVIEW", "P2P_REVIEW"] },
  { Outreach: ["NUDGE_OUTREACH", "MISSING_INFORMATION", "SCHEDULING_OUTREACH"] },
  { "Letter Writing": ["LETTER_WRITING"] },
];

// Ordering for the Reports Dashboard - Volume by Due Date Charts
export const HUMANA_ORDERED_CHART_LIST_TO_CASE_TYPE_MAPPING: {
  [key: string]: CaseType[];
}[] = [
  { "Clinical review": ["RN_REVIEW"] },
  { "MD & P2P review": ["MD_REVIEW", "P2P_REVIEW"] },
  { "Outreach (MI, P2P, Nudge)": ["NUDGE_OUTREACH", "MISSING_INFORMATION", "SCHEDULING_OUTREACH"] },
  { "Letter writing": ["LETTER_WRITING"] },
];

/*
  Query Block Generators
  Each "block" is an ES search condition
  One or more blocks are incorporated into an ES query
*/

// Create search conditions to filter to a specific time period (left and right inclusive)
const dateQueryBlocks = (
  days: string,
  dateField: string,
  historical?: boolean,
  offset?: number
): GenericCondition[] => {
  const { start, end } = dateFilterProvider(days, historical, offset);
  return [
    {
      key: dateField,
      operator: "gte",
      value: start,
    },
    {
      key: dateField,
      operator: "lte",
      value: end,
    },
  ];
};

const dateFilterProvider = (days: string, historical?: boolean, offset?: number) => {
  const daysNumber = Number(days);
  const dayOffset = daysNumber - 1;
  const date = new Date();
  /* 
    Historical means: [timeInThePast, rightNow]
    As opposed to:    [rightNow,      timeInTheFuture]
  */
  const end = historical ? Date.now() : endOfDay(date.setDate(date.getDate() + daysNumber)).getTime();
  const start = historical
    ? addMinutes(startOfDate(date.setDate(date.getDate() - dayOffset)), offset || 0).getTime()
    : Date.now();
  return {
    start,
    end,
  };
};

// Generic function to create a search condition
export const queryBlockMapper = (
  key: string,
  operator: AbstractOperator,
  value: string | string[] | number | boolean
): GenericCondition => {
  return { key, operator, value };
};

// If the chart spans 1 day, use report data from the hourly rollup job instead
// of the daily rollup job.
const durationQueryBlock = (days: string) => queryBlockMapper("durationInMinutes", "eq", days === "1" ? 60 : 1440);

const caseStatusBlocks = (statuses: CaseStatus[]) =>
  statuses.map((status) => queryBlockMapper("caseStatus.keyword", "eq", status));

// Translate QM queue filters to search conditions
const mappedQueueFilterBlocks = (filters: Filter[]) =>
  filters.flatMap((filter): GenericCondition[] => {
    const operator = queryToAbstractOperatorMapping[filter.queryOperator];
    if (!operator) {
      return [];
    }
    // Queues may have a singluar filterValue or a list of filterValues
    const value = filter.filterValue || filter.filterValues || [];
    return [queryBlockMapper(`${filter.attribute}.keyword`, operator, value)];
  });

const notManagedBySFQueryBlock = queryBlockMapper("managedBySF", "eq", false);

/*
  Query Generators
  Combine multiple search conditions into a single ES query
  Can instruct ES to aggregate data before returning it
  Customized for each chart or stats display
*/

// Cases for Reports Dashboard - Stats Bar
export const dueDateStatsQueryGenerator = () => {
  const caseStatusQueryBlocks = caseStatusBlocks(["COMPLETE", "CANCELLED"]);
  const elasticStatsSearchPayload: ElasticStatsSearchPayload = {
    index: "servicecase",
    wrapWithFilterClause: true,
    filterOn: [
      {
        clause: "MUST",
        conditions: [notManagedBySFQueryBlock],
      },
      {
        clause: "MUST_NOT",
        conditions: caseStatusQueryBlocks,
      },
    ],
    aggregations: [
      {
        fieldName: "caseType.keyword",
        name: "caseTypes",
      },
    ],
  };
  return elasticStatsSearchPayload;
};

// Cases for Reports Dashboard - Queue-specific Stats Bar
export const dueDateStatsQueueBasedQueryGenerator = (
  filterClauses: ClausalCondition[],
  aggregations?: Aggregation[]
) => {
  const caseStatusQueryBlocks = caseStatusBlocks(["COMPLETE", "CANCELLED"]);
  const elasticStatsSearchPayload: ElasticStatsSearchPayload = {
    index: "servicecase",
    wrapWithFilterClause: true,
    filterOn: [
      {
        clause: "MUST",
        conditions: [notManagedBySFQueryBlock],
      },
      ...filterClauses,
      {
        clause: "MUST_NOT",
        conditions: caseStatusQueryBlocks,
      },
    ],
    aggregations: aggregations,
  };
  return elasticStatsSearchPayload;
};

// Cases for Reports Dashboard - Volume by Due Date Charts
export const dueDatesChartQueryGenerator = (days: string, caseTypes: CaseType[]) => {
  const dateBlocks = dateQueryBlocks(days, "dueDateAsLong");
  const caseStatusQueryBlocks = caseStatusBlocks(["COMPLETE", "CANCELLED"]);
  const elasticStatsSearchPayload: ElasticStatsSearchPayload = {
    index: "servicecase",
    wrapWithFilterClause: true,
    filterOn: [
      {
        clause: "MUST",
        conditions: [
          notManagedBySFQueryBlock,
          ...dateBlocks,
          {
            key: "caseType.keyword",
            operator: "inlist",
            value: caseTypes,
          },
        ],
      },
      {
        clause: "MUST_NOT",
        conditions: caseStatusQueryBlocks,
      },
    ],
    aggregations: [
      {
        fieldName: "dueDateAsLong",
        name: "dueDates",
      },
    ],
  };
  return elasticStatsSearchPayload;
};

// Cases for Reports Dashboard - Queue-specific Historical Production Chart
export const dueDatesChartQueueBasedQueryGenerator = (days: string, filterClauses: ClausalCondition[]) => {
  const dateBlocks = dateQueryBlocks(days, "dueDateAsLong");
  const caseStatusQueryBlocks = caseStatusBlocks(["COMPLETE", "CANCELLED"]);
  const elasticStatsSearchPayload: ElasticStatsSearchPayload = {
    index: "servicecase",
    wrapWithFilterClause: true,
    filterOn: [
      {
        clause: "MUST",
        conditions: [notManagedBySFQueryBlock, ...dateBlocks],
      },
      ...filterClauses,
      {
        clause: "MUST_NOT",
        conditions: caseStatusQueryBlocks,
      },
    ],
    aggregations: [
      {
        fieldName: "dueDateAsLong",
        name: "dueDates",
      },
    ],
  };
  return elasticStatsSearchPayload;
};

// Cases for Reports Dashboard - Queue-specific Volume Stats Table
export const historicalQueueSkillsAggQueryGenerator = (
  days: string,
  filterClauses: ClausalCondition[],
  dateField: string,
  notStatus?: CaseStatus[],
  skillSet?: string
) => {
  const dateBlocks = dateQueryBlocks(days, dateField, true);
  const caseStatusQueryBlocks = notStatus ? caseStatusBlocks(notStatus) : null;
  const skillSetQueryBlocks = [];
  if (skillSet) {
    skillSetQueryBlocks.push(queryBlockMapper("requiredSkills.keyword", "eq", skillSet));
  }

  const elasticStatsSearchPayload: ElasticStatsSearchPayload = {
    index: "servicecase",
    wrapWithFilterClause: true,
    filterOn: [
      {
        clause: "MUST",
        conditions: [notManagedBySFQueryBlock, ...dateBlocks, ...skillSetQueryBlocks],
      },
      ...filterClauses,
    ],
    aggregations: [
      {
        fieldName: "requiredSkills.keyword",
        name: "requiredSkills",
      },
    ],
  };

  if (caseStatusQueryBlocks) {
    elasticStatsSearchPayload.filterOn?.push({
      clause: "MUST_NOT",
      conditions: caseStatusQueryBlocks,
    });
  }

  return elasticStatsSearchPayload;
};

// Cases for Productivity Dashboard - Case Count Chart (completed cases)
export const historicalQueueProductionQueryGenerator = (
  days: string,
  filterClauses: ClausalCondition[],
  skills?: string[],
  agents?: User[]
) => {
  const dateBlocks = dateQueryBlocks(days, "outcome.dateCompletedAsLong", true);
  const caseStatusQueryBlocks = caseStatusBlocks(["COMPLETE"]);
  const skillSetQueryBlocks = [];
  if (skills && skills.length > 0 && skills[0] !== "All") {
    skillSetQueryBlocks.push(queryBlockMapper("requiredSkills.keyword", "inlist", skills));
  }
  const agentFilterBlocks = [];
  if (agents && agents.length > 0 && agents[0] !== ("All" as User)) {
    agentFilterBlocks.push(
      queryBlockMapper(
        "name.keyword",
        "inlist",
        agents.map((agent) => agent.name || "")
      )
    );
  }
  const elasticStatsSearchPayload: ElasticStatsSearchPayload = {
    index: "servicecase",
    wrapWithFilterClause: true,
    filterOn: [
      {
        clause: "MUST",
        conditions: [
          notManagedBySFQueryBlock,
          ...dateBlocks,
          ...skillSetQueryBlocks,
          ...agentFilterBlocks,
          ...caseStatusQueryBlocks,
        ],
      },
      ...filterClauses,
    ],
    aggregations: [
      {
        fieldName: "outcome.dateCompletedAsLong",
        name: "dateCompleted",
        aggregations: [
          {
            fieldName: "totalTimeWorkedInSeconds",
            name: "timeWorked",
            type: "SUM",
          },
        ],
      },
    ],
  };
  return elasticStatsSearchPayload;
};

// Cases for Productivity Dashboard - Case Count Chart (incoming cases)
export const incomingQueryGenerator = (days: string, filterClauses: ClausalCondition[], skills?: string[]) => {
  const dateBlocks = dateQueryBlocks(days, "dateCreatedAsLong", true);
  const skillSetQueryBlocks = [];
  if (skills && skills.length > 0 && skills[0] !== "All") {
    skillSetQueryBlocks.push(queryBlockMapper("requiredSkills.keyword", "inlist", skills));
  }
  const elasticStatsSearchPayload: ElasticStatsSearchPayload = {
    index: "servicecase",
    wrapWithFilterClause: true,
    filterOn: [
      {
        clause: "MUST",
        conditions: [notManagedBySFQueryBlock, ...dateBlocks, ...skillSetQueryBlocks],
      },
      ...filterClauses,
    ],
    aggregations: [
      {
        fieldName: "dateCreatedAsLong",
        name: "dateCreated",
      },
    ],
  };
  return elasticStatsSearchPayload;
};

// Time tracker activity for Productivity Dashboard - Hours Worked Chart
export const workingTimeQueryGenerator = (days: string, agents: User[], queueId: string) => {
  const dateBlocks = dateQueryBlocks(days, "dateCreatedAsLong", true, 13);
  const durationBlocks = [durationQueryBlock(days)];
  const queueFilterQueryBlocks = [queryBlockMapper("queueId.keyword", "eq", queueId)];
  const agentFilterBlocks = [];
  if (agents.length > 0 && agents[0] !== ("All" as User)) {
    agentFilterBlocks.push(
      queryBlockMapper(
        "name.keyword",
        "inlist",
        agents.map((agent) => agent.name || "")
      )
    );
  }
  const elasticStatsSearchPayload: ElasticStatsSearchPayload = {
    index: "useractivityreport",
    wrapWithFilterClause: true,
    filterOn: [
      {
        clause: "MUST",
        conditions: [...dateBlocks, ...queueFilterQueryBlocks, ...agentFilterBlocks, ...durationBlocks],
      },
    ],
    aggregations: [
      {
        fieldName: "windowStart",
        name: "windowStart",
        aggregations: [
          {
            fieldName: "workingMinutes",
            name: "workingTime",
            type: "SUM",
          },
          {
            fieldName: "idleMinutes",
            name: "idleTime",
            type: "SUM",
          },
        ],
      },
    ],
  };
  return elasticStatsSearchPayload;
};

//todo check this
export const caseStatsDataMapper = (
  searchResponse: ElasticStatsSearchResponse,
  caseMappingList: {
    [key: string]: string[];
  }[]
) => {
  const result: { [key: string]: number } = {};
  caseMappingList.forEach((caseTypeMapping) => {
    const caseLabel = Object.keys(caseTypeMapping)[0];
    let count = 0;
    caseTypeMapping[caseLabel].forEach((caseType) => {
      if (searchResponse.aggregations?.caseTypes?.[caseType]) {
        count += searchResponse.aggregations?.caseTypes[caseType]["count"];
      }
    });
    result[caseLabel] = count;
  });
  return result;
};

/*
  Data Mappers
  Convert ES response into a format usable by Recharts.
  To create a daily chart, Recharts requires 1 data point per day on the x-axis.
  But ES only returns data for days where data exists.
  Therefore for days without data, we pad with a zero value (count = 0).
  For single-day charts, the x-axis time unit is hours instead of days.
*/

export const caseChartDataMapper = (aggregationData: AggregationData, days: string, historical?: boolean) => {
  if (days !== "1") {
    return multiDayChartDataMapper(aggregationData, days, historical);
  } else {
    return singleDayChartDataMapper(aggregationData);
  }
};

const multiDayChartDataMapper = (aggregationData: AggregationData, days: string, historical?: boolean) => {
  // Create array with 1 element per day on the chart, as required by Recharts.
  const baseCaseDateArray = [];
  for (let index = 0; index < Number(days); index++) {
    baseCaseDateArray.push({ name: todayPlusOrMinusDayNumberStringGenerator(index, historical), count: 0 });
  }

  // Convert the ES response into the same format as the baseCaseDateArray.
  // If multiple data points are on the same day, group them by summing all
  // the values.
  const aggregationKeys = Object.keys(aggregationData ?? {});
  const compiledData = [];
  for (const key of aggregationKeys) {
    const aggregationRecord = aggregationData?.[key] ?? { count: 0 };
    // Handle the scenario of either receiving the millis, UTC date or string millis
    let formattedDate: Date | number | null = null;
    if (/^\d+$/.test(key)) {
      const dateMillis = parseInt(key);
      formattedDate = new Date(dateMillis);
    } else {
      const nonUTCDate = key.split(".")[0];
      formattedDate = parseISO(nonUTCDate);
    }
    const item: ChartItem = {
      name: formattedDate.toDateString(),
      count: aggregationRecord["count"] || 0,
    };
    const itemIndex = compiledData.findIndex((element) => element.name === item.name);
    if (itemIndex >= 0) {
      compiledData[itemIndex].count += item.count;
      if (aggregationRecord["aggregations"]) {
        for (const aggName in aggregationRecord["aggregations"]) {
          compiledData[itemIndex]["aggregations"]![aggName] += aggregationRecord["aggregations"]?.[aggName]?.["value"]!;
        }
      }
    } else {
      if (aggregationRecord["aggregations"]) {
        item.aggregations = {};
        for (const aggName in aggregationRecord["aggregations"]) {
          item["aggregations"][aggName] = aggregationRecord["aggregations"]?.[aggName]?.["value"] || 0;
        }
      }
      compiledData.push(item);
    }
  }

  // Lodash will ignore objects from baseCaseDateArray if an object with the
  // same `name` already exists in compiledData.
  const result = unionBy(compiledData, baseCaseDateArray, "name").sort(
    (a, b) => new Date(a.name).getTime() - new Date(b.name).getTime()
  );
  return result;
};

const singleDayChartDataMapper = (aggregationData: AggregationData) => {
  // Create array with 1 element per hour, from 00:00 to the current hour
  const baseCaseHourArray = [];
  const hourOfDay = new Date().getHours();
  for (let index = 0; index <= hourOfDay; index++) {
    baseCaseHourArray.push({ name: todayPlusOrMinusHourNumberStringGenerator(index), count: 0 });
  }

  const compiledData = [];
  for (const key in aggregationData) {
    //have to do in order to handle longs and / or dates
    const formattedDate = /^\d+$/.test(key) ? Number(key) : key;
    // have to do in order to round exact dates times into an exact hour
    const date = new Date(formattedDate);
    date.setHours(date.getHours() + Math.floor(date.getMinutes() / 60));
    date.setMinutes(0, 0, 0);
    const item: ChartItem = {
      name: date.toTimeString(),
      count: aggregationData[key]["count"] || 0,
    };
    const itemIndex = compiledData.findIndex((element) => element.name === item.name);
    if (itemIndex >= 0) {
      compiledData[itemIndex].count += item.count;
      if (aggregationData[key]["aggregations"]) {
        for (const aggName in aggregationData[key]["aggregations"]) {
          compiledData[itemIndex]["aggregations"]![aggName] +=
            aggregationData[key]["aggregations"]?.[aggName]["value"]!;
        }
      }
    } else {
      if (aggregationData[key]["aggregations"]) {
        item.aggregations = {};
        for (const aggName in aggregationData[key]["aggregations"]) {
          item["aggregations"][aggName] = aggregationData[key]["aggregations"]?.[aggName]["value"] || 0;
        }
      }
      compiledData.push(item);
    }
  }

  let result = unionBy(compiledData, baseCaseHourArray, "name").sort(
    (a, b) => Number((a.name as string).slice(0, 2)) - Number((b.name as string).slice(0, 2))
  );

  const dataStart = result.findIndex((data) => data.count !== 0);
  return result.slice(dataStart);
};

// Utility functions

/* 
  Most fax queues filter out cases created in the last 2 minutes so scheduled
  jobs (ML) can finish processing attachments before someone works the case.
  Exception is Highmark which delays for 5 min.
  This function removes the filter because we want to see all cases.
*/
// Removes relative-time filters before building stats queries.
export const filtersWithoutFaxAutoAttachFilter = (filters: Filter[]) => {
  const faxAttachFilterIndex = filters?.findIndex((filter) => filter.filterValueIsRelativeTime);
  if (faxAttachFilterIndex && faxAttachFilterIndex >= 0) {
    filters.splice(faxAttachFilterIndex, 1);
  }
  return filters;
};

// Returns ClausalConditions from the queue's effective filter source, stripping the fax auto-attach filter
export const getQueueFilterClausesWithoutFaxAutoAttach = (queue: QueueFilterSource): ClausalCondition[] => {
  if (queue.useFilterTree && queue.filterTree) {
    return convertFilterTreeToClausalConditions(queue.filterTree);
  }
  return getQueueFilterClauses({ ...queue, filters: filtersWithoutFaxAutoAttachFilter(queue.filters?.slice() || []) });
};

const todayPlusOrMinusDayNumberStringGenerator = (additionalDays: number, historical?: boolean) => {
  return addDays(new Date(), historical ? -additionalDays : additionalDays).toDateString();
};

const todayPlusOrMinusHourNumberStringGenerator = (additionalHours: number) => {
  return addHours(startOfDate(new Date(Date.now())), additionalHours).toTimeString();
};
